/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useSession } from '@wiggot/auth/hooks/useSession'
import { useRoles } from '@wiggot/auth/hooks/useRoles'

export interface ITrackEventParams {
  name: string
  eventProperties?: any
  configs?: { path?: boolean; login: boolean }
  hasUserProperties?: boolean
}

/* The base context to feed the provider */
const AmplitudeContext = createContext<{
  isInitialized: boolean
  trackEvent: (params: ITrackEventParams) => void
  instance?: amplitude.AmplitudeClient
}>({ trackEvent: () => null, isInitialized: false })

/* A simple hook for use the context of the amplitude provider */
export const useAmplitude = () => {
  return useContext(AmplitudeContext)
}
export interface IUserPropertiesAmplitude {
  isAdminAgency?: boolean
  isIndependentAgent?: boolean
  email?: string
}

/* The amplitude provide to config and tracking events */
export const AmplitudeProvider = ({
  children,
  userId,
  amplitudeKey,
  stage,
  userProperties
}: {
  children: React.ReactNode
  userId: string | undefined
  amplitudeKey: string
  stage?: string
  userProperties?: IUserPropertiesAmplitude
}) => {
  const { profile } = useSession()
  const { isAdminAgency, isIndependentAgent } = useRoles()
  const [isInitialized, setIsInitialized] = useState(false)
  const [instance, setInstance] = useState<any>()

  const extractDomain = (email: string): string | null => {
    const regex = /@([\w-]+\.[\w.-]+)/
    const match = email.match(regex)
    return match ? match[1] : null
  }

  const getUserProperties = useCallback(() => {
    const domain = profile?.email ? extractDomain(profile?.email) : ''
    return {
      isAdminAgency,
      isIndependentAgent,
      domain
    }
  }, [isAdminAgency, isIndependentAgent, profile?.email])

  const trackEvent = useCallback(
    ({
      name,
      eventProperties = {},
      hasUserProperties = false,
      configs
    }: ITrackEventParams) => {
      const { pathname } = window.location
      if (configs) {
        if (configs.path) {
          eventProperties.path = pathname
        }
        if (configs.login) {
          eventProperties.login = Boolean(userId)
        }
      }
      instance?.logEvent(name, eventProperties)
      if (hasUserProperties) {
        instance?.setUserProperties(getUserProperties())
      }
    },
    [getUserProperties, instance, userId]
  )

  useEffect(() => {
    const init = async () => {
      try {
        if (window && amplitudeKey?.length) {
          const amplitude = await import('amplitude-js').then((m) => m.default)
          amplitude.getInstance?.().init(amplitudeKey, userId, {
            includeReferrer: true,
            disableCookies: true
          })
          if (amplitude.getInstance?.().getUserId() !== userId) {
            amplitude.getInstance?.().setUserId(userId ?? null)
          }
          if (userProperties) {
            const domain = userProperties.email
              ? extractDomain(userProperties.email)
              : ''
            amplitude.getInstance?.().setUserProperties({
              ...userProperties,
              domain,
              email: undefined
            })
          }
          if (amplitude.getInstance) {
            setInstance(amplitude.getInstance())
            setIsInitialized(true)
          }
        }
      } catch (error) {
        console.log(error)
      }
    }

    init()
  }, [amplitudeKey, userId, userProperties])

  const contextValue = useMemo(
    () => ({ trackEvent, isInitialized, instance }),
    [trackEvent, isInitialized, instance]
  )
  return (
    <AmplitudeContext.Provider value={contextValue}>
      {children}
    </AmplitudeContext.Provider>
  )
}

export default AmplitudeProvider
